import { action, computed, makeObservable, observable } from 'mobx';
import { socialAuthError, socialAuthSuccess } from '@wix/bi-logger-hls2/v2';
import { RootStore } from './root';
import { EnterPasswordStore } from './enterPassword';
import { extractErrorKeyByErrorCode } from '../utils/errorHandler';
import {
  FactorType,
  StateMachineResponse,
  StateType,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { ROUTES } from '../routes';
import { factorsMapIamToLegacy, TWOFA_PROCESS } from '../utils/constants';

export class RequireFirstFactorStore
  extends EnterPasswordStore
  implements AuthStore
{
  public stateToken: string | null = null;
  public isLoading: boolean = false;

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      email: computed,
      stateToken: observable,
      isLoading: observable,
      titleKey: computed,
      submit: action.bound,
      enableAccess: action.bound,
      setStateToken: action.bound,
      clear: action.bound,
      onInitFields: action.bound,
    });
  }

  public setStateToken(token: string) {
    this.stateToken = token;
  }

  public clear(): void {
    this.passwordField.clear();
    this.stateToken = null;
  }

  public onInitFields(): void {
    this.passwordField.clear();
  }

  get titleKey(): string {
    return 'account_verification.title';
  }

  public async submit() {
    if (!this.isSubmittable || this.hasError) {
      this.passwordField.markFieldAsDirty();
      return;
    }

    if (!this.stateToken) {
      this.passwordField.addError(this.rootStore.i18n.t('errors.general'));
      return;
    }

    this.isLoading = true;

    try {
      const response = await this.rootStore.apiStore.verifyPasswordFactor(
        this.passwordField.value,
        this.stateToken,
      );

      const data: StateMachineResponse = response.data;

      // Handle response - state can be a string or StateType enum
      const state = data.state as string;

      if (state === StateType.SUCCESS || state === 'SUCCESS') {
        // Login successful, redirect to post-login
        this.rootStore.biLogger.report(
          socialAuthSuccess({
            socialNetwork: this.rootStore.activeSocialProvider,
            context: this.rootStore.emailStepStore.getContext(),
          }),
        );
        const additionalData = data.additionalData ?? {};
        return this.rootStore.navigationStore.postLogin({
          uaToken: additionalData.uaToken?.strValue,
          authMethod: additionalData.authMethod?.strValue,
        });
      } else if (
        state === StateType.MFA_CHALLENGE ||
        state === 'MFA_CHALLENGE'
      ) {
        // MFA_CHALLENGE - has mfaChallengeData but may not have twoFAToken
        // Call resendSecondFactorCode to get twoFAToken (required by enableAccess)
        this.rootStore.fedopsLogger.interactionStarted(TWOFA_PROCESS);
        await this.initiateMfaChallengeFromChallengeData(data);
      } else if (state === StateType.REQUIRE_MFA || state === 'REQUIRE_MFA') {
        // REQUIRE_MFA - need to initiate challenge first to get proper challenge data
        // This also ensures we pick a factor with code input (not PUSH)
        this.rootStore.fedopsLogger.interactionStarted(TWOFA_PROCESS);
        await this.initiateMfaChallenge(data);
      } else {
        console.error(
          'RequireFirstFactorStore: unexpected IAM state',
          state,
          data,
        );
        this.passwordField.addError(this.rootStore.i18n.t('errors.general'));
      }
    } catch (error: any) {
      const errorCode =
        error.response?.data?.details?.applicationError?.code ??
        error.response?.data?.details?.applicationError?.data?.rule;

      this.rootStore.biLogger.report(
        socialAuthError({
          socialNetwork: this.rootStore.activeSocialProvider,
          reason: errorCode || 'merge_password_failed',
        }),
      );
      this.passwordField.addError(
        this.rootStore.i18n.t(extractErrorKeyByErrorCode(errorCode)),
      );
    } finally {
      this.isLoading = false;
    }
  }

  public enableAccess(): boolean {
    return !!this.stateToken && !!this.email;
  }

  get email() {
    return this.rootStore.userDataStore.email;
  }

  /**
   * Navigates to 2FA page with proper payload.
   * For IAM flow, twoFAToken might be empty but stateToken is set.
   * Use stateToken as twoFAToken fallback so enableAccess() passes.
   */
  private navigateToTwoFAWithPayload(payload: any, twoFAMethod: string) {
    // Ensure twoFAToken is set - use stateToken as fallback for IAM flow
    const fixedPayload = {
      ...payload,
      twoFAToken: payload.twoFAToken || payload.stateToken,
      currentTwoFAMethod: payload.currentTwoFAMethod || twoFAMethod,
    };

    this.rootStore.twoFactorAuthStore.setAuthParams(fixedPayload);
    this.rootStore.navigationStore.navigate(ROUTES.TWO_FACTOR_AUTH);
  }

  /**
   * Initiates MFA challenge when we get MFA_CHALLENGE response.
   * MFA_CHALLENGE has mfaChallengeData with factorType already selected,
   * but may not have twoFAToken. Call resendSecondFactorCode to get twoFAToken.
   */
  private async initiateMfaChallengeFromChallengeData(
    data: StateMachineResponse,
  ) {
    const factorType = data.mfaChallengeData?.factorType;
    if (!factorType) {
      console.error(
        'RequireFirstFactorStore: MFA_CHALLENGE response missing factorType',
        data,
      );
      this.passwordField.addError(this.rootStore.i18n.t('errors.general'));
      return;
    }

    // Convert IAM factor type to legacy method name
    const twoFAMethod = factorsMapIamToLegacy[factorType];

    try {
      // Call resendSecondFactorCode to get challenge data with twoFAToken
      const challengeResponse =
        await this.rootStore.apiStore.resendSecondFactorCode({
          twoFAToken: '', // Not needed for IAM flow, stateToken is used instead
          twoFAMethod,
          stateToken: data.stateToken as string,
        });

      if (challengeResponse.success && challengeResponse.payload) {
        this.navigateToTwoFAWithPayload(challengeResponse.payload, twoFAMethod);
      } else {
        console.error(
          'RequireFirstFactorStore: resendSecondFactorCode returned success=false for MFA_CHALLENGE',
          challengeResponse,
        );
        this.passwordField.addError(this.rootStore.i18n.t('errors.general'));
      }
    } catch (error: any) {
      const errorCode =
        error.response?.data?.details?.applicationError?.code ??
        error.response?.data?.details?.applicationError?.data?.rule;
      this.passwordField.addError(
        this.rootStore.i18n.t(extractErrorKeyByErrorCode(errorCode)),
      );
    }
  }

  /**
   * Initiates MFA challenge when we get REQUIRE_MFA response.
   * REQUIRE_MFA only has requireMfaData.availableFactors, but no actual challenge data.
   * We need to call resendSecondFactorCode to initiate the challenge and get proper data.
   * Also skips PUSH factor since it requires special handling and doesn't have code input.
   */
  private async initiateMfaChallenge(data: StateMachineResponse) {
    const availableFactors = data.requireMfaData?.availableFactors;
    if (!availableFactors || availableFactors.length === 0) {
      console.error(
        'RequireFirstFactorStore: REQUIRE_MFA response has no availableFactors',
        data,
      );
      this.passwordField.addError(this.rootStore.i18n.t('errors.general'));
      return;
    }

    // Find a factor with code input (skip PUSH since it doesn't have code input)
    const codeBasedFactor = availableFactors.find(
      (f) => f.factorType && f.factorType !== FactorType.PUSH,
    );

    const factorType =
      codeBasedFactor?.factorType || availableFactors[0].factorType;
    if (!factorType) {
      console.error(
        'RequireFirstFactorStore: no valid factorType in availableFactors',
        availableFactors,
      );
      this.passwordField.addError(this.rootStore.i18n.t('errors.general'));
      return;
    }

    // Convert IAM factor type to legacy method name
    const twoFAMethod = factorsMapIamToLegacy[factorType];

    try {
      // Call resendSecondFactorCode to initiate the challenge
      const challengeResponse =
        await this.rootStore.apiStore.resendSecondFactorCode({
          twoFAToken: '', // Not needed for IAM flow, stateToken is used instead
          twoFAMethod,
          stateToken: data.stateToken as string,
        });

      if (challengeResponse.success && challengeResponse.payload) {
        this.navigateToTwoFAWithPayload(challengeResponse.payload, twoFAMethod);
      } else {
        console.error(
          'RequireFirstFactorStore: resendSecondFactorCode returned success=false for REQUIRE_MFA',
          challengeResponse,
        );
        this.passwordField.addError(this.rootStore.i18n.t('errors.general'));
      }
    } catch (error: any) {
      const errorCode =
        error.response?.data?.details?.applicationError?.code ??
        error.response?.data?.details?.applicationError?.data?.rule;
      this.passwordField.addError(
        this.rootStore.i18n.t(extractErrorKeyByErrorCode(errorCode)),
      );
    }
  }
}
